import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  useTheme,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useState, useEffect, useMemo } from 'react';
import { SearchRequest, useConfig } from '../../api/api';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Cookies from 'universal-cookie';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import BallotIcon from '@mui/icons-material/Ballot';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ContactsIcon from '@mui/icons-material/Contacts';
import Groups3Icon from '@mui/icons-material/Groups3';
import { useTranslation } from 'react-i18next';
import { hasPermissions } from '../../auth/hasPermissions';

// Entity types with their permission keys
const ALL_ENTITY_TYPES = [
  { value: 'projects', label: 'Projects', icon: AccountTreeIcon, type: 'project', permission: 'project' },
  { value: 'leads', label: 'Leads', icon: Groups3Icon, type: 'lead', permission: 'lead' },
  { value: 'invoices', label: 'Invoices', icon: ReceiptIcon, type: 'invoice', permission: 'invoice' },
  { value: 'offers', label: 'Offers', icon: DescriptionIcon, type: 'offer', permission: 'offer' },
  { value: 'orders', label: 'Orders', icon: BallotIcon, type: 'order', permission: 'order' },
  { value: 'companies', label: 'Companies', icon: ApartmentIcon, type: 'company', permission: 'company' },
  { value: 'contacts', label: 'Contacts', icon: ContactsIcon, type: 'contact', permission: 'contact' },
];

// Hook to get available entity types based on permissions
export const useAvailableEntityTypes = () => {
  return useMemo(() => {
    return ALL_ENTITY_TYPES.filter(entity => hasPermissions(entity.permission, 'read'));
  }, []);
};

export const ChatEntityPicker = ({ open, onClose, onSelect }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter entity types based on permissions
  const entityTypes = useMemo(() => {
    return ALL_ENTITY_TYPES
      .filter(entity => hasPermissions(entity.permission, 'read'))
      .map(entity => ({
        ...entity,
        color: colors.colorPool?.[ALL_ENTITY_TYPES.indexOf(entity)] || '#00A5E3',
      }));
  }, [colors.colorPool]);

  const [selectedType, setSelectedType] = useState(() => entityTypes[0]?.value || '');

  // Update selectedType if it becomes unavailable
  useEffect(() => {
    if (selectedType && !entityTypes.find(t => t.value === selectedType)) {
      setSelectedType(entityTypes[0]?.value || '');
    }
  }, [entityTypes, selectedType]);

  const currentTypeConfig = entityTypes.find(t => t.value === selectedType);

  // Load results when picker opens or type/query changes
  useEffect(() => {
    if (!open || !config?.searchUrl || !selectedType) {
      setResults([]);
      return;
    }

    const cookies = new Cookies();
    const prefix = cookies.get('searchPrefix') || '';

    // Use shorter delay for initial load (no query), longer for search
    const delay = searchQuery ? 300 : 0;

    const timeoutId = setTimeout(() => {
      setIsLoading(true);

      // Leads need to search both company_leads and person_leads
      if (selectedType === 'leads') {
        Promise.all([
          new Promise((resolve) => {
            SearchRequest({
              baseUrl: config.searchUrl,
              path: `indexes/${prefix}company_leads/search`,
              method: 'post',
              data: { q: searchQuery || '', limit: 5 },
              onResponse: (res) => resolve((res.hits || []).map(h => ({ ...h, leadType: 'company_lead' }))),
              onError: () => resolve([]),
            });
          }),
          new Promise((resolve) => {
            SearchRequest({
              baseUrl: config.searchUrl,
              path: `indexes/${prefix}person_leads/search`,
              method: 'post',
              data: { q: searchQuery || '', limit: 5 },
              onResponse: (res) => resolve((res.hits || []).map(h => ({ ...h, leadType: 'person_lead' }))),
              onError: () => resolve([]),
            });
          }),
        ]).then(([companyLeads, personLeads]) => {
          setResults([...companyLeads, ...personLeads]);
          setIsLoading(false);
        });
      } else {
        SearchRequest({
          baseUrl: config.searchUrl,
          path: `indexes/${prefix}${selectedType}/search`,
          method: 'post',
          data: { q: searchQuery || '', limit: 10 },
          onResponse: (res) => {
            setResults(res.hits || []);
            setIsLoading(false);
          },
          onError: () => setIsLoading(false),
        });
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedType, config?.searchUrl, open]);

  const handleSelect = (item) => {
    // For leads, use the specific lead type (company_lead or person_lead)
    const type = item.leadType || currentTypeConfig.type;
    onSelect({
      type,
      id: item.id,
      label: item.label || item.name || item.nr || `#${item.id}`,
    });
  };

  const handleClose = () => {
    setSearchQuery('');
    setResults([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.glass,
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>{t('Link Entity')}</Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: colors.text, opacity: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Entity Type Selector */}
        <Box className="flex flex-wrap gap-2 mb-3">
          {entityTypes.map(type => {
            const Icon = type.icon;
            const isSelected = selectedType === type.value;
            return (
              <Box
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-all"
                sx={{
                  backgroundColor: isSelected ? `${type.color}25` : colors.bgInput,
                  border: `1px solid ${isSelected ? `${type.color}50` : 'transparent'}`,
                  '&:hover': {
                    backgroundColor: `${type.color}20`,
                    border: `1px solid ${type.color}40`,
                  },
                }}
              >
                <Icon sx={{ fontSize: 16, color: isSelected ? type.color : colors.text, opacity: isSelected ? 1 : 0.6 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? type.color : colors.text,
                    opacity: isSelected ? 1 : 0.7,
                  }}
                >
                  {t(type.label)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder={t('Search...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.text, opacity: 0.5, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.bgInput,
              borderRadius: '10px',
              '& fieldset': { border: 'none' },
            },
          }}
        />

        <List sx={{ maxHeight: 300, overflow: 'auto', mx: -2 }}>
          {results.length === 0 && !isLoading && (
            <Typography sx={{ p: 2, textAlign: 'center', color: colors.text, opacity: 0.5 }}>
              {t('No results found')}
            </Typography>
          )}
          {results.map((item) => {
            const Icon = currentTypeConfig?.icon;
            return (
              <ListItemButton
                key={item.id}
                onClick={() => handleSelect(item)}
                sx={{
                  borderRadius: '10px',
                  mx: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: `${currentTypeConfig?.color}15`,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {Icon && (
                    <Box
                      className="flex items-center justify-center rounded-lg"
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: `${currentTypeConfig?.color}20`,
                      }}
                    >
                      <Icon sx={{ color: currentTypeConfig?.color, fontSize: 18 }} />
                    </Box>
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label || item.name || item.nr}
                  secondary={item.subLabel}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.6 } }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
};
